import { Link } from "react-router-dom";
import { Reactions } from "./Reactions";
import { ProfileAvatar } from "../../components/ProfileAvatar";
import { useProfiles } from "../goals/api";
import { goalPillColor } from "../goals/requiredGoals";
import { Card, Spinner, Tag } from "../../components/ui";
import { relativeTime } from "../../lib/time";
import { usePhotoUrl, type FeedEntry } from "../checkins/api";
import {
  discardPending,
  flushOutbox,
  type PendingCheckIn,
} from "../../lib/outbox";
import type { Profile } from "../../lib/database.types";

/**
 * A post: who, which goal, when, the photo full-bleed, the note and reactions.
 * Per the Pact handoff (4b).
 */
export function CheckInRow({ entry }: { entry: FeedEntry }) {
  const photo = usePhotoUrl(entry.photo_path);
  const owner = useProfiles().data?.find((profile) => profile.id === entry.owner_id);

  return (
    <article className="overflow-hidden rounded-[20px] border border-[#ececef] bg-white">
      <div className="flex items-center gap-2.5 px-3.5 py-3">
        <ProfileAvatar name={entry.owner_name} avatarPath={owner?.avatar_path} size={28} ring={0} />
        <Link
          to={`/people/${entry.owner_id}`}
          className="shrink-0 text-sm font-semibold text-[#0a0a0b] no-underline"
        >
          {entry.owner_name}
        </Link>
        <Link
          to={`/goals/${entry.goal_id}`}
          className="min-w-0 truncate rounded-full border px-2 py-0.5 text-[11px] font-medium text-[#0a0a0b] no-underline"
          style={goalPillColor(entry.goal_title)}
        >
          {entry.goal_title}
        </Link>
        <span className="ml-auto shrink-0 font-['Geist_Mono',ui-monospace,monospace] text-xs text-[#8a8a94]">
          {shortAge(entry.checked_in_at)}
        </span>
      </div>

      {entry.photo_path &&
        (photo.data ? (
          <img
            src={photo.data}
            alt={`Proof for ${entry.goal_title}`}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        ) : (
          <div className="flex aspect-[4/3] items-center justify-center bg-[#f3f3f5]">
            <Spinner />
          </div>
        ))}

      <div className="flex flex-col gap-3 px-3.5 pb-3.5 pt-3">
        {entry.note && <p className="text-sm leading-[1.45] text-[#0a0a0b]">{entry.note}</p>}
        <Reactions checkInId={entry.id} ownerId={entry.owner_id} />
      </div>
    </article>
  );
}

/**
 * A check-in that is still in the local outbox. Dashed and slightly faded —
 * present and counted, just not visible to anyone else yet.
 */
export function PendingRow({
  entry,
  profile,
}: {
  entry: PendingCheckIn;
  profile?: Profile | null;
}) {
  const failed = entry.permanent;

  return (
    <Card
      className={`gap-2 border-dashed !bg-white p-3.5 ${
        failed
          ? "border-accent-400 opacity-100"
          : "border-neutral-400 opacity-85"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <ProfileAvatar
            name={profile?.display_name ?? "You"}
            avatarPath={profile?.avatar_path}
            size={28}
            ring={0}
          />
          <span className="shrink-0 text-[13px] font-semibold">
            {profile ? `${profile.display_name} (you)` : "You"}
          </span>
          <Tag tone="neutral" className="max-w-full truncate">
            {entry.goal_title.toLowerCase()}
          </Tag>
        </div>
        <span className="shrink-0 text-[11px] opacity-50">
          {failed ? "couldn't save" : "↻ pending sync"}
        </span>
      </div>

      {entry.note && <p className="text-[13px] opacity-75">{entry.note}</p>}

      {failed ? (
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-[11px] opacity-50">
            {entry.last_error}
          </p>
          <button
            type="button"
            onClick={() => void discardPending(entry.id)}
            className="shrink-0 text-[11px] text-accent-800 hover:underline"
          >
            discard
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <p className="text-[11px] opacity-50">
            {entry.attempts > 0
              ? `${entry.attempts} attempts`
              : "queued on this device"}
          </p>
          <button
            type="button"
            onClick={() => void flushOutbox()}
            className="text-[11px] text-accent-700 hover:underline"
          >
            retry now
          </button>
        </div>
      )}
    </Card>
  );
}

/** "8m", "2h", "4d" — the feed writes ages without the trailing "ago". */
function shortAge(iso: string): string {
  return relativeTime(iso).replace(/ ago$/, "");
}
